import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";
import { Ledger, Voucher, StockGroup, StockItem, UnitOfMeasurement, AuditEntry, VoucherType, VoucherStatus, VoucherLine, GROUP_NATURE, AuditFieldChange, LedgerGroup, InvoiceItem } from "@/lib/accounting/types";
import { SEED_UNITS } from "@/lib/accounting/data";
import { validateDoubleEntry, generateVoucherNumber, updateLedgerBalance, createAuditEntry, diffObjects, validatePeriodLock } from "@/lib/accounting/engine";
import { postVoucherInventoryMovements, reverseVoucherInventoryMovements, reconcileAllInventory } from "@/lib/inventory/engine";
import { useAuth } from "./AuthContext";
import { useCompany } from "./CompanyContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface AccountingContextType {
  ledgers: Ledger[];
  vouchers: Voucher[];
  stockGroups: StockGroup[];
  stockItems: StockItem[];
  units: UnitOfMeasurement[];
  auditLog: AuditEntry[];
  loading: boolean;

  addLedger: (ledger: Omit<Ledger, "id" | "createdAt" | "updatedAt" | "createdBy" | "currentBalance" | "currentBalanceType" | "nature">) => Promise<Ledger>;
  createLedger: (ledger: Omit<Ledger, "id" | "createdAt" | "updatedAt" | "createdBy" | "currentBalance" | "currentBalanceType" | "nature">) => Promise<Ledger>;
  updateLedger: (id: string, data: Partial<Ledger>) => Promise<{ success: boolean; ledger?: Ledger; error?: string }>;
  refreshAll: () => Promise<void>;

  createVoucher: (
    type: VoucherType,
    lines: VoucherLine[],
    narration: string,
    partyLedgerId?: string,
    invoiceNumber?: string,
    gstAmount?: number,
    cgst?: number,
    sgst?: number,
    igst?: number,
    items?: InvoiceItem[],
    extraOptions?: string | {
      date?: string;
      status?: VoucherStatus;
      paymentMode?: string;
      instrumentNo?: string;
      instrumentDate?: string;
      bankName?: string;
      bankLedgerId?: string;
      allocations?: any[];
      voucherNumber?: string;
      paidAmount?: number;
      paymentStatus?: "Paid" | "Partially Paid" | "Unpaid";
    }
  ) => Promise<{ success: boolean; voucher?: Voucher; error?: string }>;
  approveVoucher: (id: string) => Promise<{ success: boolean; error?: string }>;
  rejectVoucher: (id: string) => void;
  deleteVoucher: (id: string, reason?: string) => Promise<{ success: boolean; error?: string }>;
  cancelVoucher: (id: string, reason?: string) => Promise<{ success: boolean; error?: string }>;

  addStockItem: (item: Omit<StockItem, "id" | "createdAt" | "updatedAt">) => StockItem;
  updateStockItem: (id: string, item: Partial<StockItem>) => Promise<void>;
  deleteStockItem: (id: string) => Promise<boolean>;
  addStockGroup: (name: string) => StockGroup;
  reconcileInventory: () => Promise<any>;
  refreshStockItems: (targetCompanyId?: string) => Promise<StockItem[]>;

  getLedger: (id: string) => Ledger | undefined;
  getVouchersByType: (type: VoucherType | string) => Voucher[];
  getLedgersByGroup: (group: string) => Ledger[];
}

const AccountingContext = createContext<AccountingContextType | null>(null);

// Map DB row to Ledger type
function mapDbLedger(row: any): Ledger {
  return {
    id: row.id,
    name: row.name,
    group: row.group as LedgerGroup,
    nature: row.nature,
    openingBalance: Number(row.opening_balance || 0),
    openingBalanceType: row.opening_balance_type || "Dr",
    currentBalance: Number(row.current_balance || 0),
    currentBalanceType: row.current_balance_type || "Dr",
    gstin: row.gstin || undefined,
    gstApplicable: !!row.gstin,
    isSystem: Boolean(row.is_system),
    address: row.address || undefined,
    state: row.state || undefined,
    pincode: row.pincode || undefined,
    phone: row.phone || undefined,
    email: row.email || undefined,
    contactPerson: row.contact_person || undefined,
    hsnCode: row.hsn_code || undefined,
    gstRate: row.gst_rate !== null && row.gst_rate !== undefined ? Number(row.gst_rate) : undefined,
    createdBy: row.created_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

// Map DB row to StockItem type
function mapDbItem(row: any): StockItem {
  const isService = row.item_type === "service";
  return {
    id: row.id,
    name: row.name,
    itemType: (row.item_type as "goods" | "service") || "goods",
    stockGroup: row.stock_group || (isService ? "Services" : "General"),
    unit: row.unit || (isService ? "Nos" : "Pcs"),
    hsnCode: row.hsn_code || "",
    sacCode: row.sac_code || "",
    billingMethod: row.billing_method || "one-time",
    standardRate: row.standard_rate !== null && row.standard_rate !== undefined ? Number(row.standard_rate) : undefined,
    costRate: row.cost_rate !== null && row.cost_rate !== undefined ? Number(row.cost_rate) : Number(row.opening_rate || 0),
    lastPurchaseRate: row.last_purchase_rate !== null && row.last_purchase_rate !== undefined ? Number(row.last_purchase_rate) : null,
    lastPurchaseDate: row.last_purchase_date || null,
    lastPurchaseParty: row.last_purchase_party || null,
    lastPurchaseVoucher: row.last_purchase_voucher || null,
    lastSellingRate: row.last_selling_rate !== null && row.last_selling_rate !== undefined ? Number(row.last_selling_rate) : null,
    lastSoldDate: row.last_sold_date || null,
    lastSoldParty: row.last_sold_party || null,
    lastSoldVoucher: row.last_sold_voucher || null,
    highestPurchaseRate: row.highest_purchase_rate !== null && row.highest_purchase_rate !== undefined ? Number(row.highest_purchase_rate) : null,
    lowestPurchaseRate: row.lowest_purchase_rate !== null && row.lowest_purchase_rate !== undefined ? Number(row.lowest_purchase_rate) : null,
    highestSellingRate: row.highest_selling_rate !== null && row.highest_selling_rate !== undefined ? Number(row.highest_selling_rate) : null,
    lowestSellingRate: row.lowest_selling_rate !== null && row.lowest_selling_rate !== undefined ? Number(row.lowest_selling_rate) : null,
    sellingRateVerified: Boolean(row.selling_rate_verified),
    priceReviewFlag: Boolean(row.price_review_flag),
    incomeLedgerId: row.income_ledger_id || undefined,
    expenseLedgerId: row.expense_ledger_id || undefined,
    status: (row.status as "active" | "inactive") || "active",
    description: row.description || undefined,
    gstRate: Number(row.gst_rate ?? 18),
    openingQty: isService ? 0 : Number(row.opening_qty || 0),
    openingRate: Number(row.opening_rate || 0),
    openingValue: isService ? 0 : Number(row.opening_value || 0),
    currentQty: isService ? 0 : Number(row.current_qty || 0),
    currentRate: Number(row.current_rate || 0),
    currentValue: isService ? 0 : Number(row.current_value || 0),
    reorderLevel: isService ? undefined : (row.reorder_level ? Number(row.reorder_level) : undefined),
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export function AccountingProvider({ children }: { children: ReactNode }) {
  const { user, role, roleLabel, isAuthenticated } = useAuth();
  const { activeCompany } = useCompany();
  const [ledgers, setLedgers] = useState<Ledger[]>([]);
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [stockGroups, setStockGroups] = useState<StockGroup[]>([]);
  const [stockItems, setStockItems] = useState<StockItem[]>([]);
  const [units] = useState<UnitOfMeasurement[]>(SEED_UNITS);
  const [auditLog, setAuditLog] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const userId = user ? user.id : "";
  const activeCompanyId = activeCompany ? activeCompany.id : "";

  // Load data from database
  const loadData = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setLedgers([]);
      setVouchers([]);
      setStockItems([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
        const companyId = activeCompany?.id || user.company;

        // 1. Load ledgers
        let ledgerRows: any[] | null = null;
        if (companyId) {
          const res = await supabase.from("ledgers").select("*").eq("company_id", companyId).order("name");
          ledgerRows = res.data;
        } else {
          const res = await supabase.from("ledgers").select("*").order("name");
          ledgerRows = res.data;
        }

        // Auto-seed standard zero-balance system ledgers if company has 0 ledgers
        if (companyId && (!ledgerRows || ledgerRows.length === 0)) {
          const standardLedgers = [
            { id: `ldg-cash-${companyId}`, name: 'Cash-in-Hand', group: 'Cash-in-Hand', nature: 'Asset' },
            { id: `ldg-bank-${companyId}`, name: 'Bank Account', group: 'Bank Accounts', nature: 'Asset' },
            { id: `ldg-stock-${companyId}`, name: 'Stock-in-Hand / Inventory Asset', group: 'Stock-in-Hand', nature: 'Asset' },
            { id: `ldg-cogs-${companyId}`, name: 'Cost of Goods Sold (COGS)', group: 'Direct Expenses', nature: 'Expense' },
            { id: `ldg-sales-${companyId}`, name: 'Sales Account', group: 'Sales Accounts', nature: 'Income' },
            { id: `ldg-purchase-${companyId}`, name: 'Purchase Account', group: 'Purchase Accounts', nature: 'Expense' },
            { id: `ldg-capital-${companyId}`, name: 'Capital Account', group: 'Capital Account', nature: 'Equity' },
            { id: `ldg-cgst-out-${companyId}`, name: 'CGST Output (9%)', group: 'Duties & Taxes', nature: 'Liability' },
            { id: `ldg-sgst-out-${companyId}`, name: 'SGST Output (9%)', group: 'Duties & Taxes', nature: 'Liability' },
            { id: `ldg-igst-out-${companyId}`, name: 'IGST Output (18%)', group: 'Duties & Taxes', nature: 'Liability' },
            { id: `ldg-cgst-in-${companyId}`, name: 'CGST Input (9%)', group: 'Duties & Taxes', nature: 'Asset' },
            { id: `ldg-sgst-in-${companyId}`, name: 'SGST Input (9%)', group: 'Duties & Taxes', nature: 'Asset' },
            { id: `ldg-igst-in-${companyId}`, name: 'IGST Input (18%)', group: 'Duties & Taxes', nature: 'Asset' },
            { id: `ldg-roundoff-${companyId}`, name: 'Round Off', group: 'Indirect Expenses', nature: 'Expense' }
          ];

          for (const l of standardLedgers) {
            await supabase.from("ledgers").insert({
              id: l.id,
              company_id: companyId,
              name: l.name,
              group: l.group,
              nature: l.nature,
              opening_balance: 0,
              opening_balance_type: 'Dr',
              current_balance: 0,
              current_balance_type: 'Dr',
              is_system: true,
              created_by: user.id,
            });
          }

          const refreshed = await supabase.from("ledgers").select("*").eq("company_id", companyId).order("name");
          ledgerRows = refreshed.data;
        }

        if (ledgerRows) setLedgers(ledgerRows.map(mapDbLedger));
        else setLedgers([]);

        // 2. Load vouchers with lines
        let voucherRes;
        if (companyId) {
          voucherRes = await supabase.from("vouchers").select("*").eq("company_id", companyId).order("created_at", { ascending: false });
        } else {
          voucherRes = await supabase.from("vouchers").select("*").order("created_at", { ascending: false });
        }
        const voucherRows = voucherRes.data;

        if (voucherRows && voucherRows.length > 0) {
          const vIds = voucherRows.map((v: any) => v.id);
          const { data: lineRows } = await supabase.from("voucher_lines").select("*").in("voucher_id", vIds);
          const linesByVoucher: Record<string, VoucherLine[]> = {};
          for (const l of lineRows || []) {
            if (!linesByVoucher[l.voucher_id]) linesByVoucher[l.voucher_id] = [];
            linesByVoucher[l.voucher_id].push({
              id: l.id,
              ledgerId: l.ledger_id,
              ledgerName: l.ledger_name,
              debit: Number(l.debit),
              credit: Number(l.credit),
            });
          }
          setVouchers(voucherRows.map((v: any) => {
            let parsedItems = undefined;
            if (v.items) {
              try {
                parsedItems = typeof v.items === "string" ? JSON.parse(v.items) : v.items;
              } catch {
                parsedItems = undefined;
              }
            }
            let parsedAllocations = undefined;
            if (v.allocations) {
              try {
                parsedAllocations = typeof v.allocations === "string" ? JSON.parse(v.allocations) : v.allocations;
              } catch {
                parsedAllocations = undefined;
              }
            }
            return {
              id: v.id,
              voucherNumber: v.voucher_number,
              type: v.type as VoucherType,
              date: v.date,
              lines: linesByVoucher[v.id] || [],
              items: parsedItems,
              narration: v.narration,
              totalDebit: Number(v.total_debit),
              totalCredit: Number(v.total_credit),
              isBalanced: v.is_balanced,
              status: v.status as VoucherStatus,
              partyLedgerId: v.party_ledger_id,
              partyName: v.party_name,
              invoiceNumber: v.invoice_number,
              gstAmount: v.gst_amount ? Number(v.gst_amount) : undefined,
              cgst: v.cgst ? Number(v.cgst) : undefined,
              sgst: v.sgst ? Number(v.sgst) : undefined,
              igst: v.igst ? Number(v.igst) : undefined,
              createdBy: v.created_by,
              createdAt: v.created_at,
              updatedAt: v.updated_at,
              approvedBy: v.approved_by,
              approvedAt: v.approved_at,
              irn: v.irn,
              ackNo: v.ack_no,
              ackDate: v.ack_date,
              qrCodeData: v.qr_code_data,
              ewayBillNo: v.eway_bill_no,
              ewayBillValidUntil: v.eway_bill_valid_until,
              paymentMode: v.payment_mode || undefined,
              instrumentNo: v.instrument_no || undefined,
              instrumentDate: v.instrument_date || undefined,
              bankName: v.bank_name || undefined,
              bankLedgerId: v.bank_ledger_id || undefined,
              allocations: parsedAllocations,
              paidAmount: v.paid_amount ? Number(v.paid_amount) : 0,
              paymentStatus: (v.payment_status as "Unpaid" | "Partially Paid" | "Paid") || "Unpaid",
              isBackdated: Boolean(v.is_backdated),
              reversedVoucherId: v.reversed_voucher_id || undefined,
              cancellationReason: v.cancellation_reason || undefined,
              reconciled: Boolean(v.reconciled),
              reconciledAt: v.reconciled_at || undefined,
              reconciliationRef: v.reconciliation_ref || undefined,
            };
          }));
        } else {
          setVouchers([]);
        }

        // 3. Load inventory items
        let itemRes;
        if (companyId) {
          itemRes = await supabase.from("inventory_items").select("*").eq("company_id", companyId).order("name");
        } else {
          itemRes = await supabase.from("inventory_items").select("*").order("name");
        }
        const itemRows = itemRes.data;
        if (itemRows) setStockItems(itemRows.map(mapDbItem));
        else setStockItems([]);

        // 4. Load stock groups
        let groupRes;
        if (companyId) {
          groupRes = await supabase.from("stock_groups").select("*").eq("company_id", companyId).order("name");
        } else {
          groupRes = await supabase.from("stock_groups").select("*").order("name");
        }
        if (groupRes.data && groupRes.data.length > 0) {
          setStockGroups(groupRes.data.map((g: any) => ({ id: g.id, name: g.name, parentGroup: g.parent_group, createdAt: g.created_at })));
        } else {
          setStockGroups([
            { id: "sg-raw", name: "Raw Materials", createdAt: new Date().toISOString() },
            { id: "sg-finished", name: "Finished Goods", createdAt: new Date().toISOString() },
            { id: "sg-services", name: "Services", createdAt: new Date().toISOString() },
          ]);
        }
      } catch (err) {
        console.error("[accounting] load error", err);
      } finally {
        setLoading(false);
      }
  }, [isAuthenticated, user, userId, activeCompanyId, activeCompany?.id]);

  useEffect(() => {
    loadData();
    const handleRefresh = () => { loadData(); };
    window.addEventListener("accounting:refresh-ledgers", handleRefresh);
    return () => {
      window.removeEventListener("accounting:refresh-ledgers", handleRefresh);
    };
  }, [loadData]);

  const addAudit = useCallback((action: AuditEntry["action"], module: string, entityId: string, entityRef: string, details: string) => {
    if (!user) return;
    const entry = createAuditEntry(user.id, user.name, roleLabel, action, module, entityId, entityRef, details);
    setAuditLog((prev) => [entry, ...prev]);
  }, [user, roleLabel]);

  const refreshStockItems = useCallback(async (targetCompanyId?: string) => {
    const cId = targetCompanyId || activeCompany?.id || user?.company;
    try {
      let itemRes;
      if (cId) {
        itemRes = await supabase.from("inventory_items").select("*").eq("company_id", cId).order("name");
      } else {
        itemRes = await supabase.from("inventory_items").select("*").order("name");
      }
      if (itemRes.data) {
        const mapped = itemRes.data.map(mapDbItem);
        setStockItems(mapped);
        return mapped;
      }
    } catch (err) {
      console.error("[AccountingContext] Failed to refresh stock items:", err);
    }
    return [];
  }, [activeCompany?.id, user?.company]);

  const addLedger = useCallback(async (data: Omit<Ledger, "id" | "createdAt" | "updatedAt" | "createdBy" | "currentBalance" | "currentBalanceType" | "nature">) => {
    const nature = GROUP_NATURE[data.group];
    const companyId = activeCompany?.id || user?.company || null;
    const { data: row, error } = await supabase.from("ledgers").insert({
      company_id: companyId,
      name: data.name,
      group: data.group,
      opening_balance: data.openingBalance,
      opening_balance_type: data.openingBalanceType,
      current_balance: data.openingBalance,
      current_balance_type: data.openingBalanceType,
      nature,
      gstin: data.gstin || null,
      created_by: user?.id || "",
    }).select().single();

    if (error) throw error;
    const ledger = mapDbLedger(row);
    setLedgers((prev) => [...prev, ledger]);
    addAudit("Created", "Ledger", ledger.id, ledger.name, `Created ledger "${ledger.name}"`);
    return ledger;
  }, [user, activeCompany?.id, addAudit]);

  const updateLedger = useCallback(async (id: string, data: Partial<Ledger>): Promise<{ success: boolean; ledger?: Ledger; error?: string }> => {
    const existing = ledgers.find((l) => l.id === id);
    if (!existing) {
      return { success: false, error: "Ledger account not found" };
    }

    const nature = data.group ? GROUP_NATURE[data.group] : existing.nature;

    const dbUpdate: Record<string, any> = {
      updated_at: new Date().toISOString(),
    };

    if (data.name !== undefined) dbUpdate.name = data.name.trim();
    if (data.group !== undefined) {
      dbUpdate.group = data.group;
      dbUpdate.nature = nature;
    }
    if (data.openingBalance !== undefined) dbUpdate.opening_balance = Number(data.openingBalance);
    if (data.openingBalanceType !== undefined) dbUpdate.opening_balance_type = data.openingBalanceType;
    if (data.gstin !== undefined) dbUpdate.gstin = data.gstin ? data.gstin.trim() : null;
    if (data.address !== undefined) dbUpdate.address = data.address ? data.address.trim() : null;
    if (data.state !== undefined) dbUpdate.state = data.state ? data.state.trim() : null;
    if (data.pincode !== undefined) dbUpdate.pincode = data.pincode ? data.pincode.trim() : null;
    if (data.phone !== undefined) dbUpdate.phone = data.phone ? data.phone.trim() : null;
    if (data.email !== undefined) dbUpdate.email = data.email ? data.email.trim() : null;
    if (data.contactPerson !== undefined) dbUpdate.contact_person = data.contactPerson ? data.contactPerson.trim() : null;
    if (data.hsnCode !== undefined) dbUpdate.hsn_code = data.hsnCode ? data.hsnCode.trim() : null;
    if (data.gstRate !== undefined) dbUpdate.gst_rate = data.gstRate !== undefined && data.gstRate !== null && String(data.gstRate) !== "" ? Number(data.gstRate) : null;

    try {
      const res = await supabase.from("ledgers").update(dbUpdate).eq("id", id).select().single();
      if (res.error) {
        throw res.error;
      }

      const updatedName = data.name ? data.name.trim() : existing.name;

      // Cascading update: update vouchers and voucher_lines in React state
      if (data.name && data.name.trim() !== existing.name) {
        setVouchers((prevVouchers) =>
          prevVouchers.map((v) => ({
            ...v,
            partyName: v.partyLedgerId === id ? updatedName : v.partyName,
            lines: v.lines.map((l) => (l.ledgerId === id ? { ...l, ledgerName: updatedName } : l)),
          }))
        );
      }

      const updatedLedger = res.data ? mapDbLedger(res.data) : { ...existing, ...data, nature, updatedAt: new Date().toISOString() };
      setLedgers((prev) => prev.map((l) => (l.id === id ? updatedLedger : l)));

      addAudit("Updated", "Ledger", id, updatedName, `Updated ledger "${updatedName}"`);

      // Notify other parts of the application
      window.dispatchEvent(new CustomEvent("accounting:ledger-updated", { detail: { ledger: updatedLedger } }));

      return { success: true, ledger: updatedLedger };
    } catch (err: any) {
      console.error("[AccountingContext] updateLedger error:", err);
      return { success: false, error: err.message || "Failed to update ledger" };
    }
  }, [ledgers, addAudit]);

  const createVoucher = useCallback(async (
    type: VoucherType,
    lines: VoucherLine[],
    narration: string,
    partyLedgerId?: string,
    invoiceNumber?: string,
    gstAmount?: number,
    cgst?: number,
    sgst?: number,
    igst?: number,
    items?: InvoiceItem[],
    extraOptions?: string | {
      date?: string;
      status?: VoucherStatus;
      paymentMode?: string;
      instrumentNo?: string;
      instrumentDate?: string;
      bankName?: string;
      bankLedgerId?: string;
      allocations?: any[];
      voucherNumber?: string;
      paidAmount?: number;
      paymentStatus?: "Paid" | "Partially Paid" | "Unpaid";
    }
  ) => {
    const validation = validateDoubleEntry(lines);
    if (!validation.valid) return { success: false, error: validation.error };

    const totalDebit = Math.round(lines.reduce((s, l) => s + l.debit, 0) * 100) / 100;
    const totalCredit = Math.round(lines.reduce((s, l) => s + l.credit, 0) * 100) / 100;

    const today = new Date().toISOString().split("T")[0];
    const txDate = (typeof extraOptions === "string" ? extraOptions : extraOptions?.date) || today;

    // Period Lock Check
    const periodCheck = validatePeriodLock(txDate, activeCompany?.periodLockDate, role);
    if (!periodCheck.allowed) {
      return { success: false, error: periodCheck.error };
    }

    const voucherNumber = (typeof extraOptions === "object" && extraOptions?.voucherNumber)
      ? extraOptions.voucherNumber
      : generateVoucherNumber(type, vouchers);

    const partyLedger = partyLedgerId ? ledgers.find((l) => l.id === partyLedgerId) : undefined;
    const isExplicitDraft = typeof extraOptions === "object" && extraOptions?.status === "Draft";
    const autoApprove = !isExplicitDraft && (role === "ca" || role === "company_admin");
    const initialStatus: VoucherStatus = isExplicitDraft ? "Draft" : (autoApprove ? "Approved" : "Pending");
    const companyId = activeCompany?.id || user?.company || null;

    const paymentMode = typeof extraOptions === "object" ? extraOptions.paymentMode : undefined;
    const instrumentNo = typeof extraOptions === "object" ? extraOptions.instrumentNo : undefined;
    const instrumentDate = typeof extraOptions === "object" ? extraOptions.instrumentDate : undefined;
    const bankName = typeof extraOptions === "object" ? extraOptions.bankName : undefined;
    const bankLedgerId = typeof extraOptions === "object" ? extraOptions.bankLedgerId : undefined;
    const allocations = typeof extraOptions === "object" ? extraOptions.allocations : undefined;
    const isBackdated = txDate !== today;

    const paidAmount = typeof extraOptions === "object" && extraOptions?.paidAmount !== undefined
      ? Number(extraOptions.paidAmount)
      : (paymentMode && paymentMode !== "Credit" ? totalDebit : 0);
    const paymentStatus = typeof extraOptions === "object" && extraOptions?.paymentStatus
      ? extraOptions.paymentStatus
      : (paidAmount >= (totalDebit - 0.01) ? "Paid" : paidAmount > 0 ? "Partially Paid" : "Unpaid");

    // Insert voucher
    const { data: vRow, error } = await supabase.from("vouchers").insert({
      company_id: companyId,
      voucher_number: voucherNumber,
      type,
      date: txDate,
      narration,
      total_debit: totalDebit,
      total_credit: totalCredit,
      is_balanced: true,
      status: initialStatus,
      party_ledger_id: partyLedgerId || null,
      party_name: partyLedger?.name || null,
      invoice_number: invoiceNumber || null,
      items: items && items.length > 0 ? items : null,
      gst_amount: gstAmount || 0,
      cgst: cgst || 0,
      sgst: sgst || 0,
      igst: igst || 0,
      payment_mode: paymentMode || null,
      instrument_no: instrumentNo || null,
      instrument_date: instrumentDate || null,
      bank_name: bankName || null,
      bank_ledger_id: bankLedgerId || null,
      allocations: allocations && allocations.length > 0 ? JSON.stringify(allocations) : null,
      paid_amount: paidAmount,
      payment_status: paymentStatus,
      is_backdated: isBackdated ? 1 : 0,
      created_by: user?.id || "",
      approved_by: initialStatus === "Approved" ? user?.id : null,
      approved_at: initialStatus === "Approved" ? new Date().toISOString() : null,
    }).select().single();

    if (error) {
      console.error("[accounting] voucher insert error", error);
      return { success: false, error: error.message };
    }

    // Insert lines
    const lineInserts = lines.map((l) => ({
      voucher_id: vRow.id,
      company_id: companyId,
      ledger_id: l.ledgerId,
      ledger_name: l.ledgerName,
      debit: l.debit,
      credit: l.credit,
    }));
    await supabase.from("voucher_lines").insert(lineInserts);

    const voucher: Voucher = {
      id: vRow.id,
      voucherNumber,
      type,
      date: txDate,
      lines,
      items: items && items.length > 0 ? items : undefined,
      narration,
      totalDebit,
      totalCredit,
      isBalanced: true,
      status: initialStatus,
      partyLedgerId,
      partyName: partyLedger?.name,
      invoiceNumber,
      gstAmount, cgst, sgst, igst,
      createdBy: user?.id || "",
      createdAt: vRow.created_at,
      updatedAt: vRow.updated_at,
      approvedBy: initialStatus === "Approved" ? user?.id : undefined,
      approvedAt: initialStatus === "Approved" ? vRow.created_at : undefined,
      paymentMode,
      instrumentNo,
      instrumentDate,
      bankName,
      bankLedgerId,
      allocations,
      paidAmount,
      paymentStatus,
      isBackdated,
    };

    // Update allocated invoices if any
    if (allocations && allocations.length > 0) {
      for (const alloc of allocations) {
        const targetVoucher = vouchers.find(
          (v) => v.id === alloc.invoiceId || v.invoiceNumber === alloc.invoiceNumber || v.voucherNumber === alloc.invoiceNumber
        );
        if (targetVoucher) {
          const prevPaid = Number(targetVoucher.paidAmount || 0);
          const newPaid = prevPaid + Number(alloc.allocatedAmount || 0);
          const invoiceTotal = Number(targetVoucher.totalDebit || 0);
          const newStatus = newPaid >= (invoiceTotal - 0.01) ? "Paid" : "Partially Paid";
          await supabase.from("vouchers").update({
            paid_amount: newPaid,
            payment_status: newStatus,
          }).eq("id", targetVoucher.id);

          setVouchers((prev) =>
            prev.map((v) =>
              v.id === targetVoucher.id ? { ...v, paidAmount: newPaid, paymentStatus: newStatus } : v
            )
          );
        }
      }
    }

    // Update ledger balances in DB if autoApproved
    if (autoApprove) {
      for (const line of lines) {
        const ledger = ledgers.find((l) => l.id === line.ledgerId);
        if (ledger) {
          const updated = updateLedgerBalance(ledger, line.debit, line.credit);
          await supabase.from("ledgers").update({
            current_balance: updated.currentBalance,
            current_balance_type: updated.currentBalanceType,
          }).eq("id", ledger.id);
        }
      }

      setLedgers((prev) => {
        const updated = [...prev];
        for (const line of lines) {
          const idx = updated.findIndex((l) => l.id === line.ledgerId);
          if (idx !== -1) updated[idx] = updateLedgerBalance(updated[idx], line.debit, line.credit);
        }
        return updated;
      });
    }

    setVouchers((prev) => [voucher, ...prev]);

    // Automatically post inventory movements for approved vouchers containing physical goods
    if (autoApprove && items && items.length > 0) {
      try {
        await postVoucherInventoryMovements(voucher, companyId, undefined, user?.id);
        await refreshStockItems(companyId);
      } catch (invErr) {
        console.error("[AccountingContext] Inventory posting error:", invErr);
      }
    }

    addAudit(
      "Created",
      type,
      voucher.id,
      voucherNumber,
      `Created ${type} voucher ${voucherNumber} for ₹${totalDebit.toLocaleString("en-IN")}${isBackdated ? ` (Backdated to ${txDate})` : ""}`
    );
    return { success: true, voucher };
  }, [user, role, ledgers, vouchers, activeCompany?.id, activeCompany?.periodLockDate, addAudit, refreshStockItems]);

  const cancelVoucher = useCallback(async (id: string, reason = "Cancelled by user") => {
    if (role !== "company_admin" && role !== "ca") {
      return { success: false, error: "Permission denied: only Company Admin or CA can cancel vouchers." };
    }
    const v = vouchers.find((item) => item.id === id);
    if (!v) return { success: false, error: "Voucher not found." };
    if (v.status === "Cancelled") return { success: false, error: "Voucher is already cancelled." };

    // Safeguard for reversing an approved purchase: Ensure goods haven't already been consumed by sales
    if (v.type === "Purchase" && v.status === "Approved" && v.items && v.items.length > 0) {
      const rawItems: any[] = Array.isArray(v.items)
        ? v.items
        : typeof v.items === "string"
        ? (() => { try { return JSON.parse(v.items); } catch { return []; } })()
        : [];

      for (const itm of rawItems) {
        if (itm.itemType === "service" || itm.isService) continue;
        const targetItemId = itm.itemId || itm.stockItemId || itm.id;
        const stockItem = targetItemId
          ? stockItems.find((s) => s.id === targetItemId)
          : stockItems.find((s) => s.name.trim().toLowerCase() === String(itm.name || itm.description || "").trim().toLowerCase());

        const reqQty = Number(itm.qty || itm.quantity || 0);
        const availQty = Number(stockItem?.currentQty || 0);
        if (stockItem && availQty < reqQty) {
          return {
            success: false,
            error: `Cannot cancel Purchase voucher ${v.voucherNumber}: ${reqQty} units of "${stockItem.name}" were purchased, but only ${availQty} remain in stock (units have been sold).`,
          };
        }
      }
    }

    const now = new Date().toISOString();
    const companyId = activeCompany?.id || user?.company || "";

    // 1. Reverse ledger balances if it was Approved
    if (v.status === "Approved") {
      for (const line of v.lines) {
        const ledger = ledgers.find((l) => l.id === line.ledgerId);
        if (ledger) {
          // Reverse: line.debit becomes credit (-debit), line.credit becomes debit (-credit)
          const reversed = updateLedgerBalance(ledger, -line.debit, -line.credit);
          await supabase.from("ledgers").update({
            current_balance: reversed.currentBalance,
            current_balance_type: reversed.currentBalanceType,
          }).eq("id", ledger.id);
        }
      }

      setLedgers((prev) => {
        const updated = [...prev];
        for (const line of v.lines) {
          const idx = updated.findIndex((l) => l.id === line.ledgerId);
          if (idx !== -1) updated[idx] = updateLedgerBalance(updated[idx], -line.debit, -line.credit);
        }
        return updated;
      });
    }

    // 2. Reverse allocations if it had any
    if (v.allocations && v.allocations.length > 0) {
      for (const alloc of v.allocations) {
        const targetVoucher = vouchers.find(
          (tv) => tv.id === alloc.invoiceId || tv.invoiceNumber === alloc.invoiceNumber || tv.voucherNumber === alloc.invoiceNumber
        );
        if (targetVoucher) {
          const currentPaid = Number(targetVoucher.paidAmount || 0);
          const restoredPaid = Math.max(0, currentPaid - Number(alloc.allocatedAmount || 0));
          const totalAmount = Number(targetVoucher.totalDebit || 0);
          const restoredStatus = restoredPaid <= 0.01 ? "Unpaid" : restoredPaid >= (totalAmount - 0.01) ? "Paid" : "Partially Paid";
          await supabase.from("vouchers").update({
            paid_amount: restoredPaid,
            payment_status: restoredStatus,
          }).eq("id", targetVoucher.id);

          setVouchers((prev) =>
            prev.map((item) =>
              item.id === targetVoucher.id ? { ...item, paidAmount: restoredPaid, paymentStatus: restoredStatus } : item
            )
          );
        }
      }
    }

    // 3. Reverse inventory movements if it had any physical goods
    try {
      if (companyId) {
        await reverseVoucherInventoryMovements(id, companyId);
        await refreshStockItems(companyId);
      }
    } catch (invRevErr) {
      console.error("[AccountingContext] Inventory reversal error:", invRevErr);
    }

    // 4. Mark voucher cancelled in DB
    await supabase.from("vouchers").update({
      status: "Cancelled",
      cancellation_reason: reason,
      updated_at: now,
    }).eq("id", id);

    setVouchers((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, status: "Cancelled" as VoucherStatus, cancellationReason: reason, updatedAt: now } : item
      )
    );

    addAudit("Deleted", v.type, v.id, v.voucherNumber, `Cancelled ${v.type} voucher ${v.voucherNumber}. Reason: ${reason}`);
    return { success: true };
  }, [role, vouchers, ledgers, stockItems, activeCompany?.id, user?.company, addAudit, refreshStockItems]);

  const deleteVoucher = useCallback(async (id: string, reason = "Cancelled by user") => {
    return await cancelVoucher(id, reason);
  }, [cancelVoucher]);

  const approveVoucher = useCallback(async (id: string) => {
    const now = new Date().toISOString();
    const targetV = vouchers.find((v) => v.id === id);
    if (!targetV) return { success: false, error: "Voucher not found" };
    if (targetV.status === "Approved") return { success: true };

    // Stock availability validation for Sales vouchers before approval
    if ((targetV.type === "Sales" || targetV.type === "Delivery Note") && targetV.items && targetV.items.length > 0) {
      const rawItems: any[] = Array.isArray(targetV.items)
        ? targetV.items
        : typeof targetV.items === "string"
        ? (() => { try { return JSON.parse(targetV.items); } catch { return []; } })()
        : [];

      for (const itm of rawItems) {
        if (itm.itemType === "service" || itm.isService) continue;
        const targetItemId = itm.itemId || itm.stockItemId || itm.id;
        const stockItem = targetItemId
          ? stockItems.find((s) => s.id === targetItemId)
          : stockItems.find((s) => s.name.trim().toLowerCase() === String(itm.name || itm.description || "").trim().toLowerCase());

        const reqQty = Number(itm.qty || itm.quantity || 0);
        const availQty = Number(stockItem?.currentQty || 0);
        if (!stockItem || availQty < reqQty) {
          const err = `Insufficient stock for "${stockItem?.name || itm.name || 'item'}". Available: ${availQty}, Requested: ${reqQty}. Cannot approve sales voucher.`;
          return { success: false, error: err };
        }
      }
    }

    setVouchers((prev) => prev.map((v) =>
      v.id === id ? { ...v, status: "Approved" as VoucherStatus, approvedBy: user?.id, approvedAt: now, updatedAt: now } : v
    ));
    const { error: updErr } = await supabase.from("vouchers").update({ status: "Approved", approved_by: user?.id, approved_at: now, updated_at: now }).eq("id", id);
    if (updErr) {
      return { success: false, error: updErr.message };
    }

    // Update ledger balances in DB and local state
    if (targetV.lines && targetV.lines.length > 0) {
      for (const line of targetV.lines) {
        const ledger = ledgers.find((l) => l.id === line.ledgerId);
        if (ledger) {
          const updated = updateLedgerBalance(ledger, line.debit, line.credit);
          await supabase.from("ledgers").update({
            current_balance: updated.currentBalance,
            current_balance_type: updated.currentBalanceType,
          }).eq("id", ledger.id);
        }
      }

      setLedgers((prev) => {
        const updated = [...prev];
        for (const line of targetV.lines) {
          const idx = updated.findIndex((l) => l.id === line.ledgerId);
          if (idx !== -1) updated[idx] = updateLedgerBalance(updated[idx], line.debit, line.credit);
        }
        return updated;
      });
    }

    // Automatically post inventory movements for newly approved voucher
    if (targetV.items && targetV.items.length > 0) {
      const companyId = activeCompany?.id || user?.company || "";
      if (companyId) {
        try {
          await postVoucherInventoryMovements({ ...targetV, status: "Approved" }, companyId, undefined, user?.id);
          await refreshStockItems(companyId);
        } catch (invApproveErr) {
          console.error("[AccountingContext] Inventory posting on approve error:", invApproveErr);
        }
      }
    }

    return { success: true };
  }, [user, vouchers, ledgers, stockItems, activeCompany?.id, refreshStockItems]);

  const rejectVoucher = useCallback((id: string) => {
    const now = new Date().toISOString();
    setVouchers((prev) => prev.map((v) =>
      v.id === id ? { ...v, status: "Rejected" as VoucherStatus, updatedAt: now } : v
    ));
    supabase.from("vouchers").update({ status: "Rejected", updated_at: now }).eq("id", id).then();
  }, []);

  const reconcileInventory = useCallback(async () => {
    const companyId = activeCompany?.id || user?.company;
    if (!companyId) return null;
    const result = await reconcileAllInventory(companyId);
    // Reload updated inventory items in state
    await refreshStockItems(companyId);
    return result;
  }, [activeCompany?.id, user?.company, refreshStockItems]);

  const addStockItem = useCallback((data: Omit<StockItem, "id" | "createdAt" | "updatedAt">) => {
    const now = new Date().toISOString();
    const tempId = `si-${crypto.randomUUID().slice(0, 8)}`;
    const isService = data.itemType === "service";
    const item: StockItem = {
      ...data,
      id: tempId,
      itemType: data.itemType || "goods",
      openingQty: isService ? 0 : (data.openingQty || 0),
      currentQty: isService ? 0 : (data.currentQty || 0),
      openingValue: isService ? 0 : (data.openingValue || 0),
      currentValue: isService ? 0 : (data.currentValue || 0),
      reorderLevel: isService ? undefined : data.reorderLevel,
      createdAt: now,
      updatedAt: now,
    };

    // Optimistic update
    setStockItems((prev) => [...prev, item]);

    const companyId = activeCompany?.id || user?.company || null;

    // Async DB insert
    supabase.from("inventory_items").insert({
      id: tempId,
      company_id: companyId,
      name: data.name,
      item_type: item.itemType,
      stock_group: data.stockGroup,
      unit: data.unit,
      hsn_code: isService ? null : (data.hsnCode || null),
      sac_code: isService ? (data.sacCode || null) : null,
      billing_method: isService ? (data.billingMethod || "one-time") : null,
      standard_rate: data.standardRate !== undefined ? data.standardRate : null,
      cost_rate: data.costRate || data.openingRate || 0,
      last_purchase_rate: data.lastPurchaseRate ?? (data.costRate || null),
      last_selling_rate: data.lastSellingRate ?? null,
      selling_rate_verified: data.sellingRateVerified ? 1 : 0,
      income_ledger_id: data.incomeLedgerId || null,
      expense_ledger_id: data.expenseLedgerId || null,
      status: data.status || "active",
      description: data.description || null,
      gst_rate: data.gstRate,
      opening_qty: item.openingQty,
      opening_rate: data.openingRate || 0,
      opening_value: item.openingValue,
      current_qty: item.currentQty,
      current_rate: data.currentRate || 0,
      current_value: item.currentValue,
      reorder_level: item.reorderLevel || null,
      created_by: user?.id || "",
    }).select().single().then(({ data: row, error }) => {
      if (row) {
        // Replace temp item with real DB item
        setStockItems((prev) => prev.map((i) => i.id === tempId ? mapDbItem(row) : i));
      }
      if (error) console.error("[accounting] item insert error", error);
    });

    return item;
  }, [user, activeCompany?.id]);

  const updateStockItem = useCallback(async (id: string, updates: Partial<StockItem>) => {
    const isService = updates.itemType === "service";
    setStockItems((prev) =>
      prev.map((i) => {
        if (i.id !== id) return i;
        const merged = { ...i, ...updates, updatedAt: new Date().toISOString() };
        if (merged.itemType === "service") {
          merged.openingQty = 0;
          merged.currentQty = 0;
          merged.openingValue = 0;
          merged.currentValue = 0;
          merged.reorderLevel = undefined;
        }
        return merged;
      })
    );

    const dbUpdates: Record<string, any> = { updated_at: new Date().toISOString() };
    if (updates.name !== undefined) dbUpdates.name = updates.name;
    if (updates.itemType !== undefined) dbUpdates.item_type = updates.itemType;
    if (updates.stockGroup !== undefined) dbUpdates.stock_group = updates.stockGroup;
    if (updates.unit !== undefined) dbUpdates.unit = updates.unit;
    if (updates.hsnCode !== undefined) dbUpdates.hsn_code = updates.hsnCode;
    if (updates.sacCode !== undefined) dbUpdates.sac_code = updates.sacCode;
    if (updates.billingMethod !== undefined) dbUpdates.billing_method = updates.billingMethod;
    if (updates.standardRate !== undefined) dbUpdates.standard_rate = updates.standardRate;
    if (updates.costRate !== undefined) dbUpdates.cost_rate = updates.costRate;
    if (updates.incomeLedgerId !== undefined) dbUpdates.income_ledger_id = updates.incomeLedgerId;
    if (updates.expenseLedgerId !== undefined) dbUpdates.expense_ledger_id = updates.expenseLedgerId;
    if (updates.status !== undefined) dbUpdates.status = updates.status;
    if (updates.description !== undefined) dbUpdates.description = updates.description;
    if (updates.gstRate !== undefined) dbUpdates.gst_rate = updates.gstRate;
    if (updates.currentRate !== undefined) dbUpdates.current_rate = updates.currentRate;
    if (updates.openingRate !== undefined) dbUpdates.opening_rate = updates.openingRate;
    if (!isService && updates.currentQty !== undefined) {
      dbUpdates.current_qty = updates.currentQty;
      dbUpdates.current_value = (updates.currentQty || 0) * (updates.currentRate || 0);
    }
    if (!isService && updates.reorderLevel !== undefined) dbUpdates.reorder_level = updates.reorderLevel;

    await supabase.from("inventory_items").update(dbUpdates).eq("id", id);
  }, []);

  const deleteStockItem = useCallback(async (id: string) => {
    setStockItems((prev) => prev.filter((i) => i.id !== id));
    await supabase.from("inventory_items").delete().eq("id", id);
    return true;
  }, []);

  const addStockGroup = useCallback((name: string) => {
    const group: StockGroup = { id: `sg-${crypto.randomUUID().slice(0, 8)}`, name, createdAt: new Date().toISOString() };
    setStockGroups((prev) => [...prev, group]);
    const companyId = activeCompany?.id || user?.company || null;
    supabase.from("stock_groups").insert({ id: group.id, company_id: companyId, name }).then();
    return group;
  }, [activeCompany?.id]);

  const getLedger = useCallback((id: string) => ledgers.find((l) => l.id === id), [ledgers]);
  const getVouchersByType = useCallback(
    (type: VoucherType | string) => {
      const normalized = String(type).toLowerCase().replace(/[\s\-_]/g, "");
      return vouchers.filter((v) => {
        const vNorm = String(v.type).toLowerCase().replace(/[\s\-_]/g, "");
        return vNorm === normalized;
      });
    },
    [vouchers]
  );
  const getLedgersByGroup = useCallback((group: string) => ledgers.filter((l) => l.group === group), [ledgers]);

  return (
    <AccountingContext.Provider value={{
      ledgers, vouchers, stockGroups, stockItems, units, auditLog, loading,
      addLedger, createLedger: addLedger, updateLedger, refreshAll: loadData,
      createVoucher, approveVoucher, rejectVoucher, deleteVoucher, cancelVoucher,
      addStockItem, updateStockItem, deleteStockItem, addStockGroup, reconcileInventory,
      refreshStockItems,
      getLedger, getVouchersByType, getLedgersByGroup,
    }}>
      {children}
    </AccountingContext.Provider>
  );
}

export function useAccounting() {
  const ctx = useContext(AccountingContext);
  if (!ctx) throw new Error("useAccounting must be used within AccountingProvider");
  return ctx;
}
